<template>
  <section v-show="expanded" class="lightning-import" :class="{ 'is-dragging': dragging }" :aria-busy="busy" aria-label="Import products"
    @dragover.prevent="dragging = !busy" @dragleave.prevent="leaveDropzone" @drop.prevent.stop="dropFile">
    <input ref="fileInput" type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="sr-only" tabindex="-1" aria-hidden="true" :disabled="busy" @change="onFileChange" />

    <template v-if="mode === 'file'">
      <div class="import-file-content">
        <span class="import-symbol"><Icon :name="busy ? 'reload' : 'document-file'" :class="{ 'animate-spin': busy }" aria-hidden="true" /></span>
        <div class="import-intro">
          <h3>{{ busy ? 'Matching your products…' : dragging ? 'Drop your file to import' : 'Drop your order file here' }}</h3>
          <p>{{ description }}</p>
          <div class="import-actions">
            <button ref="chooseFileButton" type="button" class="import-button" :disabled="busy" @click="chooseFile">Choose file</button>
            <button type="button" class="import-button is-secondary" :disabled="busy" @click="openPaste">Paste SKUs</button>
          </div>
        </div>
        <div class="import-format" aria-label="Example file format">
          <div><span>sku</span><span>qty</span></div>
          <div><span>EXAMPLE-SKU-1</span><span>2</span></div>
          <div><span>EXAMPLE-SKU-2</span><span>10</span></div>
        </div>
      </div>
      <div class="import-file-footer">
        <p>.csv or .xlsx · Up to 2 MB · {{ maxRows }} product rows<br />Use sku and qty headings (or no header row: SKU first, quantity second). Excel imports the first worksheet.</p>
        <button type="button" class="import-link" @click="downloadTemplate()"><Icon name="download" aria-hidden="true" />Download template</button>
      </div>
    </template>

    <template v-else>
      <div class="import-paste-header">
        <div>
          <label :for="inputId">Paste SKUs and quantities</label>
          <p>One product per line. Tabs, commas or spaces all work — copy from Excel, an email or your POS export.</p>
        </div>
        <button type="button" class="import-link" :disabled="busy" @click="mode = 'file'">Upload a file instead</button>
      </div>
      <div class="import-paste-content">
        <textarea :id="inputId" ref="pasteInput" v-model="pastedText" rows="7" :disabled="busy" :aria-describedby="hintId" spellcheck="false" placeholder="EXAMPLE-SKU-1 2&#10;EXAMPLE-SKU-2, 10&#10;EXAMPLE-SKU-3" @input="error = ''" />
        <div class="import-preview" aria-live="polite">
          <span class="preview-label">Preview</span>
          <p class="preview-total"><strong>{{ preview.entries.length }}</strong> valid {{ preview.entries.length === 1 ? 'row' : 'rows' }}</p>
          <p>{{ formatCount(preview.unitCount || 0) }} units<template v-if="preview.invalidRows.length"> · {{ preview.invalidRows.length }} need correcting</template></p>
          <p :id="hintId" class="preview-hint">Missing quantities default to 1. Repeated SKUs are combined. We check each SKU against the catalog when you import; anything not found, unavailable or out of stock is listed for you to fix.</p>
          <p v-if="preview.invalidRows.length" class="preview-warning">{{ preview.invalidRows.length }} {{ preview.invalidRows.length === 1 ? 'row needs' : 'rows need' }} correcting. Valid rows can still be imported.</p>
        </div>
      </div>
      <div class="import-paste-actions">
        <button type="button" class="import-button is-secondary" :disabled="busy" @click="cancelPaste">{{ cancelLabel }}</button>
        <button type="button" class="import-button" :disabled="busy || Boolean(preview.error) || !preview.totalCount" @click="importPaste">
          <Icon v-if="busy" name="reload" class="animate-spin" aria-hidden="true" />
          {{ submitLabel }}
        </button>
      </div>
    </template>
    <p v-if="error || (mode === 'paste' && preview.error)" class="import-error" role="alert"><Icon name="info" aria-hidden="true" />{{ error || preview.error }}</p>
  </section>
</template>

<script>
import { nextTick } from "vue";
import { MAX_CSV_ROWS, downloadCsvTemplate, parsePastedText, validateCsvFile } from "@/modules/CsvOrderUpload";

export default {
  name: "LightningOrderImport",
  props: {
    expanded: { type: Boolean, default: true },
    busy: { type: Boolean, default: false },
    importFile: { type: Function, required: true },
    importText: { type: Function, required: true },
    inputId: { type: String, default: "lightning-paste-skus" },
    destinationLabel: { type: String, default: "list" },
    cancelLabel: { type: String, default: "Cancel" },
    prepareOnly: { type: Boolean, default: false },
    description: { type: String, default: "We’ll match your SKUs and fill this list. Review the products before adding them to your order." },
  },
  emits: ["import-start", "expand", "collapse"],
  data: () => ({ mode: "file", dragging: false, pastedText: "", error: "", maxRows: MAX_CSV_ROWS }),
  computed: {
    preview() { return parsePastedText(this.pastedText); },
    hintId() { return this.inputId === "lightning-paste-skus" ? "lightning-paste-hint" : `${this.inputId}-hint`; },
    submitLabel() {
      if (this.busy) return "Matching products…";
      if (!this.preview.entries.length) return "Review invalid rows";
      // These are parsed rows, not yet matched products: an unknown SKU only shows up after the lookup.
      const products = `${this.preview.entries.length} ${this.preview.entries.length === 1 ? 'row' : 'rows'}`;
      return this.prepareOnly ? `Match ${products}` : `Import ${products} to ${this.destinationLabel}`;
    },
  },
  methods: {
    downloadTemplate: downloadCsvTemplate,
    formatCount(value) { return value.toLocaleString("en-US"); },
    focusChooseFile() { this.$refs.chooseFileButton?.focus(); },
    chooseFile() {
      if (this.busy) return;
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    async openPaste() {
      this.mode = "paste";
      this.error = "";
      this.$emit("expand");
      await nextTick();
      this.$refs.pasteInput?.focus();
    },
    cancelPaste() {
      this.mode = "file";
      this.error = "";
      this.$emit("collapse");
    },
    leaveDropzone(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.dragging = false;
    },
    async uploadFile(file) {
      if (this.busy || !file) return;
      this.mode = "file";
      // Every attempt replaces the previous result, even one that fails validation.
      this.$emit("import-start");
      this.error = validateCsvFile(file) || "";
      this.$emit("expand");
      if (this.error) return;
      try {
        const result = await this.importFile(file);
        if (result.ok) this.$emit("collapse");
        else this.error = result.error;
      } catch (err) { this.error = "We couldn’t import this file. Please try again."; }
    },
    onFileChange(event) { return this.uploadFile(event.target.files[0]); },
    dropFile(event) {
      this.dragging = false;
      if (this.busy) return;
      if (event.dataTransfer.files.length !== 1) {
        this.error = "Choose one file at a time.";
        this.$emit("expand");
        return;
      }
      return this.uploadFile(event.dataTransfer.files[0]);
    },
    async importPaste() {
      if (this.busy || this.preview.error || !this.preview.totalCount) return;
      this.$emit("import-start");
      this.error = "";
      try {
        const result = await this.importText(this.pastedText);
        if (result.ok) {
          this.pastedText = "";
          this.mode = "file";
          this.$emit("collapse");
        } else this.error = result.error;
      } catch (err) { this.error = "We couldn’t import this list. Please try again."; }
    },
  },
};
</script>

<style lang="scss" scoped>
.lightning-import {
  @apply bg-white rounded-lg border-2 border-dashed border-gray-300 p-4 mb-4;
  &.is-dragging { @apply bg-theme-purple-light border-brand-indigodark; }
}
.import-file-content { @apply flex items-start gap-3 flex-wrap; }
.import-symbol { @apply inline-flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full bg-theme-purple-light text-brand-indigodarkest; svg { @apply w-5 h-5; } }
.import-intro { @apply flex-1; min-width: 220px; h3 { @apply text-base font-semibold text-brand-primary; } p { @apply text-xs text-theme-dark leading-5 mt-1; max-width: 32rem; } }
.import-actions { @apply flex flex-wrap gap-1.5 mt-3; }
.import-button { @apply inline-flex items-center justify-center gap-1.5 rounded px-3 text-xs font-medium bg-brand-primary text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-indigodark focus:ring-offset-2; min-height: 32px;
  &.is-secondary { @apply bg-gray-100 text-theme-secondary hover:bg-gray-200; }
  &:disabled { @apply opacity-50 cursor-not-allowed; }
  svg { @apply w-4 h-4; }
}
.import-format { @apply rounded border border-gray-200 text-xs text-theme-dark overflow-hidden; min-width: 190px; > div { @apply grid gap-3 px-2.5 py-1.5; grid-template-columns: 1fr 28px; } > div:first-child { @apply bg-gray-50 font-semibold; } }
.import-file-footer { @apply flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100; > p { @apply text-xs text-theme-dark leading-4; } }
.import-link { @apply inline-flex items-center justify-center gap-1.5 text-xs font-medium text-brand-indigodarkest hover:underline focus:outline-none focus:ring-2 focus:ring-brand-indigodark rounded; min-height: 28px; svg { @apply w-3.5 h-3.5; } &:disabled { @apply opacity-50 cursor-not-allowed; } }
.import-paste-header { @apply flex flex-wrap justify-between items-start gap-3; label { @apply text-base font-semibold text-brand-primary; } p { @apply text-xs leading-5 text-theme-dark mt-1; max-width: 38rem; } }
.import-paste-content { @apply grid gap-3 mt-3; grid-template-columns: minmax(0, 2fr) minmax(180px, 1fr);
  textarea { @apply w-full border border-gray-300 rounded p-3 text-xs text-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-indigodark; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; line-height: 1.5; resize: vertical; min-height: 144px; }
}
.import-preview { @apply rounded bg-gray-50 p-3 text-theme-dark; p { @apply text-xs leading-5; } .preview-hint { @apply text-xs mt-2 leading-4; } .preview-warning { @apply text-xs text-brand-primary mt-2; } }
.preview-label { @apply text-xs uppercase tracking-wider font-medium; }
.import-preview .preview-total { @apply mt-2; strong { @apply text-2xl font-semibold text-brand-primary; } }
.import-paste-actions { @apply flex flex-wrap gap-2 justify-end mt-3; }
.import-error { @apply flex items-start gap-2 text-sm text-brand-primary bg-theme-red bg-opacity-10 rounded px-4 py-3 mt-4; svg { @apply w-4 h-4 flex-shrink-0 mt-0.5; } }
@media (max-width: 640px) {
  .lightning-import { @apply p-3; }
  .import-symbol { @apply w-8 h-8; }
  .import-file-content { @apply gap-3; }
  .import-intro { min-width: 0; h3 { @apply text-base; } }
  .import-format { @apply w-full mt-1; }
  .import-paste-content { grid-template-columns: minmax(0, 1fr); }
  .import-preview { @apply p-3; }
  .import-paste-actions { @apply grid grid-cols-2; }
}
</style>
